// src/pages/StockEntry/StockEntry.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, writeBatch, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

const styles = {
    container: { fontFamily: '"Inter", sans-serif' },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
    },
    title: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#212529'
    },
    headerActions: {
        display: 'flex',
        gap: '10px'
    },
    button: { 
        padding: '10px 20px', 
        border: 'none', 
        background: '#06C167', 
        color: 'white', 
        borderRadius: '6px', 
        cursor: 'pointer', 
        fontSize: '16px',
        fontWeight: '500'
    },
    box: { 
        background: 'white', 
        padding: '30px', 
        borderRadius: '8px', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
        marginBottom: '20px' 
    },
    grid: { 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '20px', 
        marginBottom: '20px' 
    },
    formGroup: { 
        marginBottom: '15px', 
        position: 'relative' 
    },
    label: { 
        display: 'block', 
        marginBottom: '8px', 
        fontWeight: '500',
        color: '#495057'
    },
    input: { 
        width: '100%', 
        padding: '12px 15px', 
        fontSize: '16px',
        border: '1px solid #dee2e6', 
        borderRadius: '6px', 
        boxSizing: 'border-box' 
    },
    select: { width: '100%', padding: '12px 15px', fontSize: '16px', border: '1px solid #dee2e6', borderRadius: '6px', background: 'white' },
    table: { 
        width: '100%', 
        borderCollapse: 'collapse', 
        marginTop: '20px' 
    },
    th: { 
        background: '#f8f9fa', 
        padding: '12px 15px', 
        textAlign: 'left', 
        borderBottom: '2px solid #e9ecef',
        color: '#6c757d',
        fontSize: '14px',
        fontWeight: '600'
    },
    td: { 
        padding: '15px', 
        borderBottom: '1px solid #e9ecef', 
        verticalAlign: 'middle' 
    },
    suggestions: { 
        position: 'absolute', 
        background: 'white', 
        border: '1px solid #dee2e6', 
        borderRadius: '6px', 
        width: '100%', 
        maxHeight: '150px', 
        overflowY: 'auto', 
        zIndex: 100,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    },
    suggestionItem: { 
        padding: '12px 15px', 
        cursor: 'pointer' 
    },
    deleteButton: { 
        background: 'none', 
        border: 'none', 
        color: '#6c757d', 
        cursor: 'pointer', 
        fontSize: '20px' 
    },
    actionsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '30px'
    },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 },
    modalContent: { background: 'white', padding: '30px', borderRadius: '8px', width: '90%', maxWidth: '600px' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
};

function StockEntryFromExcelModal({ onClose, onSave, suppliers, isImporting, onDownloadTemplate }) {
    const [selectedSupplierId, setSelectedSupplierId] = useState('');
    const [excelFile, setExcelFile] = useState(null);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Tedarikçiden Excel ile Stok Girişi</h2>
                    <button onClick={onClose} style={{background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer'}}>&times;</button>
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Tedarikçi</label>
                    <select style={styles.select} value={selectedSupplierId} onChange={(e) => setSelectedSupplierId(e.target.value)} required>
                        <option value="">Tedarikçi Seçin...</option>
                        {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Stok Giriş Dosyası (.xlsx)</label>
                    <input type="file" accept=".xlsx, .xls" onChange={(e) => setExcelFile(e.target.files[0])} style={styles.input} />
                </div>
                <div style={styles.modalActions}>
                    <button onClick={onDownloadTemplate} style={{...styles.button, background: 'transparent', color: '#06C167', border: '1px solid #06C167', marginRight: 'auto'}}>Şablon İndir</button>
                    <button onClick={onClose} style={{...styles.button, background: '#6c757d'}}>İptal</button>
                    <button onClick={() => onSave(excelFile, selectedSupplierId)} disabled={isImporting || !excelFile || !selectedSupplierId} style={{...styles.button, background: '#198754'}}>
                        {isImporting ? 'İşleniyor...' : 'Stok Girişini Kaydet'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function StockEntry() {
    const { userData } = useAuth();
    const navigate = useNavigate();

    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);
    const [productGroups, setProductGroups] = useState([]);
    const [warehouses, setWarehouses] = useState([]); // Add warehouses state
    const [currencyRates, setCurrencyRates] = useState({ USD: 0 });
    
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [supplierNameInput, setSupplierNameInput] = useState('');
    
    const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
    const [lineItems, setLineItems] = useState([]);
    
    const [currentItem, setCurrentItem] = useState({ 
        productId: '', 
        productName: '', 
        quantity: 1,
        warehouseId: '' // Add warehouse selection for each item
    });
    const [showExcelModal, setShowExcelModal] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    useEffect(() => {
        if (!userData?.companyId) return;
        const companyId = userData.companyId;
        const unsubSuppliers = onSnapshot(query(collection(db, 'companies', companyId, 'suppliers')), snap => setSuppliers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubProducts = onSnapshot(query(collection(db, 'companies', companyId, 'products')), snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubGroups = onSnapshot(query(collection(db, 'companies', companyId, 'productGroups')), snap => setProductGroups(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        
        // Load warehouses
        const unsubWarehouses = onSnapshot(query(collection(db, 'companies', companyId, 'warehouses')), snap => setWarehouses(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        
        const companyRef = doc(db, 'companies', companyId);
        const unsubCompany = onSnapshot(companyRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().currencySettings) {
                setCurrencyRates(docSnap.data().currencySettings);
            }
        });

        return () => { unsubSuppliers(); unsubProducts(); unsubGroups(); unsubCompany(); unsubWarehouses(); }; // Add unsubWarehouses
    }, [userData]);
    
    const handleSupplierSelect = (supplier) => {
        setSupplierNameInput(supplier.name);
        setSelectedSupplier(supplier);
    };
    
    const handleProductSelect = (product) => {
        setCurrentItem({ 
            productId: product.id, 
            productName: product.name, 
            quantity: 1,
            warehouseId: '' // Reset warehouse selection
        });
    };

    const handleAddItem = () => {
        if (!currentItem.productId || currentItem.quantity <= 0) {
            toast.error("Lütfen geçerli bir ürün seçin ve miktar girin.");
            return;
        }
        setLineItems(prev => [...prev, { ...currentItem }]);
        setCurrentItem({ productId: '', productName: '', quantity: 1 });
    };

    const handleRemoveItem = (index) => {
        const newItems = [...lineItems];
        newItems.splice(index, 1);
        setLineItems(newItems);
    };

    const handleSave = async () => {
        if (!selectedSupplier || lineItems.length === 0) {
            toast.error("Lütfen bir tedarikçi seçin ve en az bir ürün ekleyin.");
            return;
        }
        
        const batch = writeBatch(db);
        const companyId = userData.companyId;

        const itemsWithPrice = lineItems.map(item => {
            const product = products.find(p => p.id === item.productId);
            const group = productGroups.find(g => g.id === product.groupId);
            const priceInfo = group?.defaultPurchasePrice || { amount: 0, currency: 'TRY' };
            
            let price = 0;
            const usdRate = currencyRates.USD || 1;

            if (priceInfo.currency === selectedSupplier.currency) {
                price = priceInfo.amount;
            } else {
                if (selectedSupplier.currency === 'TRY' && priceInfo.currency === 'USD') {
                    price = priceInfo.amount * usdRate;
                } else if (selectedSupplier.currency === 'USD' && priceInfo.currency === 'TRY') {
                    price = usdRate > 0 ? priceInfo.amount / usdRate : 0;
                }
            }
            
            return { ...item, price, total: item.quantity * price };
        });
        
        const total = itemsWithPrice.reduce((acc, item) => acc + item.total, 0);

        const invoiceRef = doc(collection(db, 'companies', companyId, 'purchaseInvoices'));
        batch.set(invoiceRef, {
            supplierId: selectedSupplier.id,
            supplierName: selectedSupplier.name,
            invoiceDate: serverTimestamp(),
            lineItems: itemsWithPrice.map(({productName, ...rest}) => rest),
            total,
            currency: selectedSupplier.currency || 'TRY',
        });

        for (const item of itemsWithPrice) {
            const productRef = doc(db, 'companies', companyId, 'products', item.productId);
            const movementRef = doc(collection(productRef, 'movements'));
            const productDoc = await getDoc(productRef);
            const productData = productDoc.data();
            
            // Update quantities based on warehouse selection
            let newTotalQuantity = productData.quantity + Number(item.quantity);
            let newWarehouseQuantities = { ...productData.warehouseQuantities } || {};
            
            if (item.warehouseId) {
                // Update specific warehouse quantity
                const currentWarehouseQuantity = newWarehouseQuantities[item.warehouseId] || 0;
                newWarehouseQuantities[item.warehouseId] = currentWarehouseQuantity + Number(item.quantity);
            }
            
            batch.update(productRef, { 
                quantity: newTotalQuantity,
                warehouseQuantities: newWarehouseQuantities
            });
            
            // Add warehouse information to movement if selected
            const movementData = {
                date: serverTimestamp(), 
                type: `Alış Faturası #${invoiceRef.id.substring(0, 6)}`, 
                quantity: `+${item.quantity} ${productData.unit || 'Adet'}` 
            };
            
            if (item.warehouseId) {
                const warehouse = warehouses.find(w => w.id === item.warehouseId);
                movementData.warehouseId = item.warehouseId;
                movementData.warehouseName = warehouse?.name || 'Depo';
            }
            
            batch.set(movementRef, movementData);
        }

        const supplierRef = doc(db, 'companies', companyId, 'suppliers', selectedSupplier.id);
        const newBalance = (selectedSupplier.balance || 0) - total;
        batch.update(supplierRef, { balance: newBalance });

        try {
            await batch.commit();
            navigate('/tedarikciler');
        } catch (error) {
            console.error("Stok girişi kaydedilirken hata:", error);
        }
    };

    const handleStockEntryImport = async (excelFile, supplierId) => {
        if (!excelFile || !supplierId) {
            toast.error("Lütfen bir tedarikçi seçin ve Excel dosyası yükleyin.");
            return;
        }
        setIsImporting(true);

        const reader = new FileReader();
        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json(worksheet);

            const companyId = userData.companyId;
            const batch = writeBatch(db);
            const selectedSupplier = suppliers.find(s => s.id === supplierId);
            let lineItems = [];

            const invoiceRef = doc(collection(db, 'companies', companyId, 'purchaseInvoices'));

            for (const item of json) {
                const product = products.find(p => p.barcode === String(item.barkod));
                if (product) {
                    const group = productGroups.find(g => g.id === product.groupId);
                    const priceInfo = group?.defaultPurchasePrice || { amount: 0, currency: 'TRY' };
                    let price = 0;
                    const usdRate = currencyRates.USD || 1;

                    if (priceInfo.currency === selectedSupplier.currency) {
                        price = priceInfo.amount;
                    } else {
                        if (selectedSupplier.currency === 'TRY' && priceInfo.currency === 'USD') {
                            price = priceInfo.amount * usdRate;
                        } else if (selectedSupplier.currency === 'USD' && priceInfo.currency === 'TRY') {
                            price = usdRate > 0 ? priceInfo.amount / usdRate : 0;
                        }
                    }
                    
                    lineItems.push({
                        productId: product.id,
                        quantity: Number(item.miktar),
                        price: price,
                        total: Number(item.miktar) * price
                    });

                    const productRef = doc(db, 'companies', companyId, 'products', product.id);
                    const newQuantity = (product.quantity || 0) + Number(item.miktar);
                    // For Excel imports, we'll update the total quantity but not specific warehouses
                    // since warehouse information is not in the Excel template
                    batch.update(productRef, { quantity: newQuantity });
                    
                    const movementRef = doc(collection(productRef, 'movements'));
                    batch.set(movementRef, {
                        date: serverTimestamp(),
                        type: `Alış Faturası #${invoiceRef.id.substring(0, 6)} (Excel ile Giriş)`,
                        quantity: `+${Number(item.miktar)} ${product.unit || 'Adet'}`
                    });
                }
            }

            const total = lineItems.reduce((acc, item) => acc + item.total, 0);
            batch.set(invoiceRef, {
                supplierId: selectedSupplier.id,
                supplierName: selectedSupplier.name,
                invoiceDate: serverTimestamp(),
                lineItems,
                total,
                currency: selectedSupplier.currency || 'TRY',
            });

            const supplierRef = doc(db, 'companies', companyId, 'suppliers', selectedSupplier.id);
            const newBalance = (selectedSupplier.balance || 0) - total;
            batch.update(supplierRef, { balance: newBalance });

            try {
                await batch.commit();
                toast.success("Stok girişi ve fatura başarıyla oluşturuldu!");
                setShowExcelModal(false);
            } catch (error) {
                console.error("Excel'den stok girişi sırasında hata:", error);
                toast.error("İşlem sırasında bir hata oluştu.");
            } finally {
                setIsImporting(false);
            }
        };
        reader.readAsArrayBuffer(excelFile);
    };

    const handleDownloadTemplate = () => {
        const templateData = [{
            barkod: '1234567890123',
            miktar: 100,
        }];
        const worksheet = XLSX.utils.json_to_sheet(templateData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Stok Giriş Şablonu");
        XLSX.writeFile(workbook, "StokGirisSablonu.xlsx");
    };

    const filteredSuppliers = supplierNameInput ? suppliers.filter(s => s.name.toLowerCase().includes(supplierNameInput.toLowerCase())) : [];
    const filteredProducts = currentItem.productName ? products.filter(p => p.name.toLowerCase().includes(currentItem.productName.toLowerCase())) : [];

    return (
        <div style={styles.container}>
            <div style={{width: '100%'}}>
                <div style={styles.header}>
                    <h1 style={styles.title}>Tedarikçiden Stok Girişi</h1>
                    <div style={styles.headerActions}>
                        <button onClick={() => setShowExcelModal(true)} style={{...styles.button, background: '#198754'}}>Excel ile Stok Girişi</button>
                    </div>
                </div>
                <div style={styles.box}>
                    <div style={styles.grid}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Tedarikçi</label>
                            <input type="text" placeholder="Tedarikçi ara..." style={styles.input} value={supplierNameInput} onChange={e => { setSupplierNameInput(e.target.value); setSelectedSupplier(null); }} required />
                            {filteredSuppliers.length > 0 && supplierNameInput && !selectedSupplier && (
                                <div style={styles.suggestions}>
                                    {filteredSuppliers.map(s => <div key={s.id} style={styles.suggestionItem} onMouseDown={() => handleSupplierSelect(s)}>{s.name}</div>)}
                                </div>
                            )}
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Giriş Tarihi</label>
                            <input style={styles.input} type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} />
                        </div>
                    </div>
                    <hr style={{border: 'none', borderTop: '1px solid #e9ecef', margin: '30px 0'}}/>
                    <h3>Ürünler</h3>
                    <div style={{...styles.grid, gridTemplateColumns: '4fr 1fr 1fr', alignItems: 'flex-end'}}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Ürün</label>
                             <input type="text" placeholder="Ürün ara..." style={styles.input} value={currentItem.productName} onChange={e => setCurrentItem(prev => ({...prev, productName: e.target.value, productId: ''}))} />
                            {filteredProducts.length > 0 && currentItem.productName && !currentItem.productId && (
                                <div style={styles.suggestions}>
                                    {filteredProducts.map(p => <div key={p.id} style={styles.suggestionItem} onMouseDown={() => handleProductSelect(p)}>{p.name}</div>)}
                                </div>
                            )}
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Miktar</label>
                            <input style={styles.input} type="number" min="1" value={currentItem.quantity} onChange={e => setCurrentItem(prev => ({...prev, quantity: Number(e.target.value)}))}/>
                        </div>
                        {/* Warehouse selection */}
                        {warehouses.filter(w => w.isActive).length > 0 && (
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Depo</label>
                                <select 
                                    style={styles.select} 
                                    value={currentItem.warehouseId} 
                                    onChange={e => setCurrentItem(prev => ({...prev, warehouseId: e.target.value}))}
                                >
                                    <option value="">Depo Seçin</option>
                                    {warehouses.filter(w => w.isActive).map(warehouse => (
                                        <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <button onClick={handleAddItem} style={{...styles.button, width: 'auto', height: '50px'}}>Ekle</button>
                    </div>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Ürün Adı</th>
                                <th style={styles.th}>Miktar</th>
                                {warehouses.filter(w => w.isActive).length > 0 && (
                                    <th style={styles.th}>Depo</th>
                                )}
                                <th style={styles.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.map((item, index) => (
                                <tr key={index}>
                                    <td style={styles.td}>{item.productName}</td>
                                    <td style={styles.td}>{item.quantity}</td>
                                    {warehouses.filter(w => w.isActive).length > 0 && (
                                        <td style={styles.td}>
                                            {item.warehouseId 
                                                ? warehouses.find(w => w.id === item.warehouseId)?.name || 'Bilinmeyen Depo' 
                                                : 'Seçilmedi'}
                                        </td>
                                    )}
                                    <td style={styles.td}><button style={styles.deleteButton} onClick={() => handleRemoveItem(index)}>&times;</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                     <div style={styles.actionsContainer}>
                        <button onClick={handleSave} style={{...styles.button, background: '#198754'}}>Stok Girişini Kaydet</button>
                     </div>
                </div>
            </div>
            {showExcelModal && <StockEntryFromExcelModal onClose={() => setShowExcelModal(false)} onSave={handleStockEntryImport} suppliers={suppliers} isImporting={isImporting} onDownloadTemplate={handleDownloadTemplate} />}
        </div>
    );
}

export default StockEntry;
